import React, { useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { transformDataForPivot } from "./transformDataForPivot";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-grid.css";

function getMonthsFromApi(apiData: any[]) {
  if (!apiData || apiData.length === 0) return [];
  return apiData?.map((item: any) => ({
    name: item.MonthYear,
    columns: ["Success", "Failure"],
  }));
}

interface PlanCohortTableProps {
  data?: any[] | null;
}

const PlanCohortTable: React.FC<PlanCohortTableProps> = ({ data }: any) => {
  const agGridRef = useRef<AgGridReact | null>(null);

  const onGridReady = (params: { api: { sizeColumnsToFit: () => void } }) => {
    params.api.sizeColumnsToFit();
  };
  const transformedData = transformDataForPivot(data);
  const months = getMonthsFromApi(data);
  const totalsIdx = transformedData.findIndex((r) => r.label === "Totals");

  const columnDefs: any = useMemo(() => {
    const baseCols = [
      {
        headerName: "",
        field: "label",
        pinned: "left",
        minWidth: 200,
        cellStyle: (params: any) => ({
          background: params.data.bg || "#fff",
          fontWeight: params.data.bold ? "bold" : "normal",
          padding: "0px 6px",
        }),
        headerStyle: { padding: "0px" },
      },
      {
        headerName: "User",
        field: "users",
        minWidth: 60,
        cellStyle: { textAlign: "center", padding: "0px" },
        headerStyle: { padding: "0px" },
      },
      {
        headerName: "Active User",
        field: "paidUsers",
        minWidth: 90,
        cellStyle: { textAlign: "center", padding: "0px" },
        headerStyle: { padding: "0px" },
      },
      {
        headerName: "InActive User",
        field: "unpaidUsers",
        minWidth: 100,
        cellStyle: { textAlign: "center", padding: "0px" },
        headerStyle: { padding: "0px" },
      },
    ];

    const monthCols = months?.map((month, mIdx) => ({
      headerName: month.name,
      headerClass: "ag-header-group-centered",
      children: [
        {
          headerName: "Auto Top-up ",
          minWidth: 120,
          cellStyle: { textAlign: "center", padding: "0px" },
          cellClass: (params: any) =>
            params.node.rowIndex > totalsIdx ? "child-summary-col" : undefined,
          valueGetter: (params: any) => params.data.monthData[mIdx]?.AutoTopup,
          cellRenderer: (params: any) => {
            const { node, data, value } = params;
            const isSummary = node.rowIndex > totalsIdx;
            if (isSummary) {
              const monthData = data.monthData[mIdx];
              return monthData?.AutoTopup || "";
            }
            return value;
          },
        },

        {
          headerName: "Auto Top-up Failed",
          minWidth: 140,
          cellStyle: { textAlign: "center", padding: "0px" },
          cellClass: (params: any) => {
            const isSummary = params.node.rowIndex > totalsIdx;
            return isSummary
              ? "child-summary-col month-group-last"
              : "month-group-last";
          },
          valueGetter: (params: any) =>
            params.data.monthData[mIdx]?.AutoTopupFailed,
          cellRenderer: (params: any) => {
            const { node, data } = params;
            const isSummary = node.rowIndex > totalsIdx;
            if (isSummary) {
              const monthData = data.monthData[mIdx];
              return monthData?.AutoTopupFailed || "";
            }
            return params.value;
          },
        },
      ],
      marryChildren: true,
    }));

    return [...baseCols, ...monthCols];
  }, [months]);

  const getRowStyle = (params: any) => {
    return {
      background: params.data.bg || undefined,
      fontWeight: params?.data?.bold ? "bold" : "normal",
    };
  };

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      autoHeight: false,
      wrapText: true,
    }),
    []
  );

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv();
    }
  };

  if (!data) return null;

  return (
    <div>
      <style>{`
        .ag-header-group-centered {
          justify-content: center !important;
          text-align: center !important;
          display: flex !important;
          align-items: center !important;
        }
        .ag-header-cell {
          padding: 0px 6px !important;
        }
        .ag-theme-alpine .ag-cell, .ag-theme-alpine .ag-header-cell, .ag-theme-alpine .ag-header-group-cell {
          border-right: 1px solid #d3d3d3 !important;
        }
        /* Remove border-right for child summary columns in month groups */
        .ag-theme-alpine .ag-cell.child-summary-col {
          border-right: none !important;
        }
        /* Add border-right after each month group */
        .ag-theme-alpine .ag-cell.month-group-last {
          border-right: 2px solid #d3d3d3 !important;
        }
          /* ✅ Center the value visually between the two month columns for summary rows */
 .ag-theme-alpine .ag-row.summary-row .ag-cell.month-group-last span {
  transform: translateX(-50%);
}
  .ag-row-bold .ag-cell-value {
  font-weight:  bold !important;
}
      `}</style>

      <div
        style={{
          display: "flex",
          justifyContent: "right",
          marginBottom: "10px",
        }}
      >
        <button
          className="small-btn"
          style={{ visibility: data ? "visible" : "hidden" }}
          onClick={() => exportGridDataAsCSV()}
        >
          Export as CSV
        </button>
      </div>

      <div className="ag-theme-alpine" style={{ width: "100%" }}>
        <AgGridReact
          ref={agGridRef}
          getRowClass={(params) => {
            let classes = [];
            if (params.data.isSummary) classes.push("summary-row");
            if (params.data.bold) classes.push("ag-row-bold");
            return classes.join(" ");
          }}
          rowData={transformedData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          suppressRowClickSelection={true}
          paginationPageSize={100}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          enableCellTextSelection={true}
        />
      </div>
    </div>
  );
};

export default PlanCohortTable;
